import DefaultImage from "@assets/tm_article_default.svg";
import Image from "@elements/ResponsiveImage/Image";
import Heading from "@elements/Typography/Heading";
import { capitalizeFirstCharOnly } from "@helper/stringTransform";
import { formatDateAgo } from "@helper/useDateFormat";
import { useMobileResponsive } from "@hooks/useDeviceType";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

import { ArticleCardProps } from "./ArticleCard";

export default function QuickNewsArticleCard({
  title,
  sectionName,
  displayWidth,
  displayHeight,
  imageField,
  imageAlt,
  linkTo,
  overrideStyle,
  dateEdited,
  dateCreated,
  refMedium,
  linkToQuick,
}: ArticleCardProps) {
  const {
    cardWrapper,
    contentWrapper,
    imageWrapperStyle,
    imageStyle,
    titleStyle,
    titleWrapper,
  } = overrideStyle || {};
  linkToQuick = linkToQuick || linkTo;
  if (refMedium) {
    linkToQuick = `${linkToQuick}?ref=${refMedium}`;
  }
  const { mounted } = useMobileResponsive();

  const layerClassByIndex = [
    "w-[calc(100%-40px)] mt-[-20px] mx-auto z-30 rounded-b-[36px] opacity-60 shadow-[0_3.6px_3.6px_0_rgba(0,0,0,0.20)]",
    "w-[calc(100%-60px)] mt-[-5px] mx-auto z-20 rounded-b-[32.4px] opacity-40 shadow-[0_3.24px_3.24px_0_rgba(0,0,0,0.20)]",
  ];

  const latestDate =
    dateEdited && dateCreated
      ? dateEdited > dateCreated
        ? dateEdited
        : dateCreated
      : dateEdited || dateCreated;

  return (
    <>
      <div className="h-full justify-center items-center">
        <div
          className={twMerge(
            "flex w-full gap-5 py-5 border-b border-gray-2250 z-50",
            cardWrapper,
          )}
        >
          <Link
            reloadDocument
            to={linkToQuick}
            className={twMerge(
              "w-full flex-shrink-0 md:flex-grow-0 sm:w-[260px]",
              imageWrapperStyle,
              "quick-news-link",
            )}
          >
            <Image
              displayWidth={displayWidth}
              srcWidth={displayWidth}
              srcHeight={displayHeight}
              src={imageField?.url ?? DefaultImage}
              alt={imageAlt ?? title}
              className={twMerge("w-full", imageStyle)}
            />
          </Link>

          <div
            className={twMerge("flex flex-col w-full gap-xs", contentWrapper)}
          >
            <div className={titleWrapper}>
              <Link reloadDocument to={linkToQuick} className="quick-news-link">
                <Heading
                  Element="h3"
                  className={twMerge(
                    "font-bold font-primary hover:underline hover:text-red-200 text-red-100",
                    titleStyle,
                  )}
                >
                  {title}
                </Heading>
              </Link>
            </div>

            {latestDate && mounted && (
              <div className="text-red-100 text-xs font-semibold">
                <span>{capitalizeFirstCharOnly(sectionName || "")}</span>
                <span className="float-right lowercase">
                  {formatDateAgo(latestDate)}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col max-w-[345px] justify-center items-center mx-auto">
          {layerClassByIndex.map((style, index) => {
            return (
              <div
                key={index}
                className={twMerge(
                  "bg-gradient-to-b from-transparent to-white-100 h-[23px] px-2.5 overflow-hidden",
                  style,
                )}
              >
                {mounted && index <= 1 && (
                  <div className="text-red-100 py-4 px-2 text-xs font-semibold"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
